import { OBJECT_ACTION_CATALOG } from "./catalog";
import { canvasEntityExtensionKindKey } from "./handlers/canvasNode";
import { OBJECT_ACTION_MANIFEST } from "./manifests";
import { mergeCanvasEntityShells } from "./mergeCanvasEntityShells";
import type {
  ActionSurface,
  ObjectActionDefinition,
  ObjectActionEntry,
  ObjectActionId,
  ObjectActionManifest,
  ObjectKind,
  ObjectKindKey,
  ObjectTypeConfig,
  StaticActionShell,
} from "./types";
import { ACTION_SURFACES, resolvePresentation } from "./types";

export const EMPTY_SHELLS: readonly StaticActionShell[] = [];

export type SurfaceIndex = Record<
  ObjectKindKey,
  Partial<Record<ActionSurface, readonly StaticActionShell[]>>
>;

function mergeActionEntries(
  base: readonly ObjectActionEntry[],
  override: readonly ObjectActionEntry[],
): ObjectActionEntry[] {
  const byId = new Map(base.map((entry) => [entry.id, entry]));
  for (const entry of override) {
    byId.set(entry.id, entry);
  }
  return [...byId.values()];
}

export function resolveObjectTypeConfig(
  kindKey: ObjectKindKey,
  manifest: ObjectActionManifest = OBJECT_ACTION_MANIFEST,
  visiting = new Set<ObjectKindKey>(),
): ObjectTypeConfig {
  const config = manifest[kindKey];
  if (!config) {
    return { actions: [] };
  }
  if (visiting.has(kindKey)) {
    return { actions: [] };
  }

  visiting.add(kindKey);

  let actions: readonly ObjectActionEntry[] = [];
  if (config.extends) {
    const parent = resolveObjectTypeConfig(config.extends, manifest, visiting);
    actions = parent.actions;
  }
  actions = mergeActionEntries(actions, config.actions);

  visiting.delete(kindKey);

  return { actions };
}

export function buildSurfaceIndex(
  manifest: ObjectActionManifest,
  catalog: Record<ObjectActionId, ObjectActionDefinition>,
): SurfaceIndex {
  const index: SurfaceIndex = {};

  for (const kindKey of Object.keys(manifest)) {
    const config = resolveObjectTypeConfig(kindKey, manifest);
    const surfaceMap: Partial<Record<ActionSurface, StaticActionShell[]>> = {};

    for (const entry of config.actions) {
      validateCatalogEntry(entry, catalog);

      for (const surface of entry.surfaces) {
        const shells = surfaceMap[surface] ?? [];
        shells.push({
          id: entry.id,
          definition: catalog[entry.id],
          presentation: resolvePresentation(entry, surface),
          hiddenWhen: entry.hiddenWhen,
          disabledWhen: entry.disabledWhen,
        });
        surfaceMap[surface] = shells;
      }
    }

    validateNoDuplicateIds(kindKey, surfaceMap);
    index[kindKey] = surfaceMap;
  }

  return index;
}

function validateCatalogEntry(
  entry: ObjectActionEntry,
  catalog: Record<ObjectActionId, ObjectActionDefinition>,
): void {
  if (!catalog[entry.id]) {
    if (import.meta.env.DEV) {
      console.warn(
        `[object-actions] Missing catalog entry for action id "${entry.id}"`,
      );
    }
  }
}

function validateNoDuplicateIds(
  kindKey: string,
  surfaceMap: Partial<Record<ActionSurface, StaticActionShell[]>>,
): void {
  if (!import.meta.env.DEV) {
    return;
  }

  for (const surface of ACTION_SURFACES) {
    const shells = surfaceMap[surface];
    if (!shells) {
      continue;
    }
    const seen = new Set<ObjectActionId>();
    for (const shell of shells) {
      if (seen.has(shell.id)) {
        console.warn(
          `[object-actions] Duplicate action "${shell.id}" for ${kindKey} × ${surface}`,
        );
      }
      seen.add(shell.id);
    }
  }
}

export const SURFACE_INDEX = buildSurfaceIndex(
  OBJECT_ACTION_MANIFEST,
  OBJECT_ACTION_CATALOG,
);

export function getStaticShells(
  kindKey: ObjectKindKey,
  surface: ActionSurface,
  kind?: ObjectKind,
): readonly StaticActionShell[] {
  const base = SURFACE_INDEX[kindKey]?.[surface] ?? EMPTY_SHELLS;

  if (kind?.type !== "canvas-node" || !kind.entityBase) {
    return base;
  }

  const extensionKey = canvasEntityExtensionKindKey(
    kind.nodeType,
    kind.entityBase,
  );
  const extension = SURFACE_INDEX[extensionKey]?.[surface] ?? EMPTY_SHELLS;

  if (extension.length === 0) {
    return base;
  }

  return mergeCanvasEntityShells(base, extension, surface);
}
